import { API_ERROR_CODES, type ApiErrorCode, type FieldError } from '@psspl/types';

/**
 * Typed failure raised by every service function.
 *
 * Screens branch on `code` (from the shared `API_ERROR_CODES` vocabulary) and never
 * on `message`, because `message` is written for humans and may change. A raw
 * network or parse failure is normalised to `SERVICE_UNAVAILABLE` / `INTERNAL_ERROR`
 * so a screen never has to handle an exception type it does not know.
 */
export class ApiClientError extends Error {
  public readonly code: ApiErrorCode;
  public readonly status: number;
  public readonly details: readonly FieldError[];
  public readonly requestId: string | null;

  constructor(options: {
    readonly code: ApiErrorCode;
    readonly message: string;
    readonly status: number;
    readonly details?: readonly FieldError[];
    readonly requestId?: string | null;
  }) {
    super(options.message);
    this.name = 'ApiClientError';
    this.code = options.code;
    this.status = options.status;
    this.details = options.details ?? [];
    this.requestId = options.requestId ?? null;
  }

  /** A previously valid session has expired or been revoked. */
  public get isSessionFailure(): boolean {
    return (
      this.code === 'UNAUTHENTICATED' ||
      this.code === 'TOKEN_EXPIRED' ||
      this.code === 'TOKEN_INVALID'
    );
  }

  /** The credentials are valid but this action is not permitted. */
  public get isPermissionFailure(): boolean {
    return this.code === 'FORBIDDEN' || this.code === 'PROJECT_ACCESS_DENIED';
  }

  public get isNotFound(): boolean {
    return this.code === 'NOT_FOUND';
  }

  public get isRateLimited(): boolean {
    return this.code === 'RATE_LIMITED';
  }

  public static from(code: string, message: string, status: number): ApiClientError {
    return new ApiClientError({
      code: isApiErrorCode(code) ? code : 'INTERNAL_ERROR',
      message,
      status,
    });
  }
}

export function isApiErrorCode(value: string): value is ApiErrorCode {
  return (API_ERROR_CODES as readonly string[]).includes(value);
}

export function isApiClientError(value: unknown): value is ApiClientError {
  return value instanceof ApiClientError;
}

/** Field-level message lookup, for binding validation errors onto form inputs. */
export function fieldErrorFor(error: unknown, field: string): string | undefined {
  if (!isApiClientError(error)) {
    return undefined;
  }

  return error.details.find((detail) => detail.field === field)?.message;
}

/** The shared error code, or `null` for a failure that never reached the API layer. */
export function errorCodeOf(error: unknown): ApiErrorCode | null {
  return isApiClientError(error) ? error.code : null;
}

/**
 * Copy for a failure, safe to show a user.
 *
 * Never returns a raw exception message: `docs/security.md` section 6 forbids
 * leaking internals, and the API message is the only string we can trust.
 *
 * Exception: a `NOT_FOUND` message is framework text for an unknown route
 * ("Cannot GET /api/…"), which names internal paths and means nothing to a user.
 * The screen's own copy is used instead; screens that need not-found wording branch
 * on the code.
 */
export function describeError(error: unknown, fallback: string): string {
  if (isApiClientError(error) && !error.isNotFound) {
    return error.message;
  }

  return fallback;
}
